/**
 * Shared animation physics constants (plan-28 §C).
 *
 * `SpatialConstants.java` is the source of truth for backend-authored
 * values; sim-only constants that mirror a backend constant note it
 * inline. A generate/check drift script (same pattern as corpus.lock)
 * is a pending governance item — until it lands, changes here must be
 * reviewed against the Java side by hand.
 *
 * Naming: numeric constants carry a unit suffix — `_FT` feet,
 * `_PX` pixels, `_DEG` degrees (check-animation-conventions.sh enforces).
 */

/** Standing/unknown car length — same default as VAL_001/VAL_002 (50.0). */
export const DEFAULT_CAR_LENGTH_FT = 50

/** Locomotive body length — mirrors PlanValidator.LOCO_LENGTH_FT (74.0). */
export const LOCO_LENGTH_FT = 74

/** Foul-point margin — mirrors PlanValidator.FOUL_CLEARANCE_FT (30.0), a
 *  stand-in for the post-demo foul_zones model. Used for REVERSE pull-clear
 *  and entry-span checks in the sim. */
export const CLEARANCE_MARGIN_FT = 30

/** Gap between a fresh drop and cars already parked at the entry end. */
export const PACK_GAP_FT = 8

/** Inter-car allowance when approximating unseeded cars at a couple
 *  junction — mirrors SpatialConstants.COUPLING_ALLOWANCE_FT (4.0). */
export const COUPLER_GAP_FT = 4

/** Max loco↔cut gap that still counts as "in coupling reach" — beyond it a
 *  COUPLE marker is physically impossible to animate honestly. Sim-only. */
export const COUPLING_REACH_FT = 150

/** Loco "parked" assumption when no position is resolvable. Sim-only. */
export const DEFAULT_LOCO_OFFSET_FT = 0

/** Longest plausible rail car for preview rendering/packing — beyond this a
 *  manifest value is treated as corrupt data (ignored + data-quality notice,
 *  never silently trusted). Real freight cars top out near 90 ft; 120 leaves
 *  headroom for oversize flatcar/autorack types. Sim-only. */
export const MAX_PLAUSIBLE_CAR_LENGTH_FT = 120

/** Low-severity advisory: a manifest length this large is plausible (kept)
 *  but unusual for yard switching — worth a data-quality glance. Real
 *  freight cars top out near 90 ft; 85 catches the tail end of flatcar/
 *  autorack types plus likely fat-fingered entries. Sim-only. */
export const UNUSUAL_CAR_LENGTH_FT = 85
